import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { TenantContext } from '../../tenant/TenantContext';
import { FormField } from '../../uiwidgets/FormField';
import { FormContainer } from '../../uiwidgets/FormContainer';
import { SubmitButton } from '../../uiwidgets/SubmitButton';
import { CancelButton } from 'uiwidgets/CancelButton';
import { Form, Row, Col, Card, Table, Button, Modal, ModalBody, CardGroup } from 'react-bootstrap';
import { API } from '../../api/API';
import { BUSINESS_DONATION_CAUSES_URL, SPONSORSHIPS_URL } from '../../config/config';
import { extractError } from '../../api/ErrorUtil';
import { isNumeric, containsAlphaNumAndSpaceOnly, containsAlphaNumAndSpaceAndPunctuationOnly } from 'util/Validators';
import _ from "lodash";
import { formatCurrency } from 'util/Currency';
import { evenRound } from 'util/NumberUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faArrowRight, faArrowLeft, faCheck, faImage, faBan, faFileDownload, faFilePdf, faImagePortrait, faFileImage, faImages } from '@fortawesome/free-solid-svg-icons';
import { RecipientForm } from './RecipientForm';
import { isValidUUID } from 'util/Validators';
import { splitCamelCase } from 'util/StringUtils';
import { getSponsorshipStatus, getRecipientTypes, getSponsorshipTypes } from 'util/Lookup';
import { FilesField } from 'uiwidgets/FilesField';
import { formatDate } from 'util/Date';
import { properCase } from 'util/StringUtils';

const BusinessSponsorship = () => {

    const PAGE_SIZE = 6;
    const initialFormState = {
        errors: {},
        infoMessage: "",
        readOnly: false,
        editMode: false,
        donationCauses: [],
        donationCauseId: '',
        recipientType: '',
        sponsorships: [],
        sponsorshipAttributes: {},
        sponsorshipType: '',
        currentPage: 0,
        sponsorshipKey: "",
        filter: {
            titleFilter: ""
        },
        randImageId: Date.now()
    };

    const initialValidationState = {
        donationCauseIdError: false,
        donationCauseIdErrMsg: "",
        recipientTypeError: false,
        recipientTypeErrMsg: "",
        importFileError: false,
        importFileErrMsg: ""
    };

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFormData({
            ...formData,
            ...messageReset()
        });
    }

    const onSpsDocToastClose = () => {
        setFormData({
            ...spsDocFormData,
            ...messageReset()
        });
    }

    const { businessConfig } = useContext(TenantContext);
    let [formData, setFormData] = useState(initialFormState);
    let [formValidityData, setFormValidityData] = useState(initialValidationState)

    let [spsDocFormData, setSpsDocFormData] = useState({ errors: {}, infoMessage: {} });

    const recipientValidationFunction = useRef(null);
    const imageFileInput = useRef(null);
    const importFileInput = useRef(null);
    const spsDocFileInput = useRef(null);

    const [showSponsorshipImage, setShowSponsorshipImage] = useState({ show: false, sponsorship: {} });
    const handleSponsorshipImageClose = () => setShowSponsorshipImage({ show: false, sponsorship: {} });
    const handleSponsorshipImageShow = (sponsorship) => {
        setShowSponsorshipImage({ show: true, sponsorship: sponsorship });
    }

    const [showSponsorshipDetail, setShowSponsorshipDetail] = useState({ show: false, sponsorship: {} });
    const handleSponsorshipDetailClose = () => setShowSponsorshipDetail({ show: false, sponsorship: {} });
    const handleSponsorshipDetailShow = (sponsorship) => {
        setShowSponsorshipDetail({ show: true, sponsorship: sponsorship });
    }

    const fetchDonationCauses = useCallback(async () => {
        try {
            let dc = await API.get(BUSINESS_DONATION_CAUSES_URL);
            setFormData({ ...formData, donationCauses: dc.data });
        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setFormData({ ...formData, infoMessage: "No donation causes were found" });
            } else {
                setFormData({ ...formData, errors: { message, errors, httpStatus } });
            }
        }

    });

    const getSponsorships = async (filterKeyword) => {
        let filter = formData.filter.titleFilter;
        if (filterKeyword) {
            filter = filterKeyword;
        }
        let sponsorshipsResp = await API.get(SPONSORSHIPS_URL + "/" + formData.donationCauseId,
            { params: { size: PAGE_SIZE, page: formData.currentPage, title: filter } });
        let sponsorships = sponsorshipsResp.data.content.map(obj => ({
            ...obj,
            maxAmount: evenRound(parseFloat(obj.maxAmount) / 100, 2)
        }));

        return { sponsorships: sponsorships, totalPages: sponsorshipsResp.data.totalPages };
    }

    const fetchSponsorships = useCallback(async () => {
        try {

            if (!formData.donationCauseId) {
                return;
            }
            let { sponsorships, totalPages } = await getSponsorships();
            setFormData({
                ...formData,
                ...messageReset(),
                sponsorships: sponsorships,
                totalPages: totalPages
            });
        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setFormData({ ...formData, sponsorships: [] });
            } else {
                setFormData({ ...formData, errors: { message, errors, httpStatus } });
            }
        }

    });

    useEffect(() => {
        fetchDonationCauses();
    }, []);

    useEffect(() => {
        fetchSponsorships();
    }, [formData.currentPage]);

    useEffect(() => {
        fetchSponsorships();
    }, [formData.donationCauseId]);

    const handleChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const name = event.target.name;
        const value = event.target.value;
        setFormData({
            ...formData,
            ...messageReset(),
            [name]: value
        })
    }

    const handleFilterKeyDown = async (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (formData.currentPage === 0) {
                let { sponsorships, totalPages } = await getSponsorships(formData.filter.titleFilter, true);
                setFormData({
                    ...formData,
                    sponsorships: sponsorships,
                    totalPages: totalPages
                });
            } else {
                setFormData({
                    ...formData,
                    currentPage: 0
                });
            }
        }
    };
    const handleFilter = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const name = event.target.name;
        const value = event.target.value;
        if (value.length > 3) {
            try {

                if (formData.currentPage === 0) {
                    let { sponsorships, totalPages } = await getSponsorships(value);
                    setFormData({
                        ...formData,
                        sponsorships: sponsorships,
                        totalPages: totalPages,
                        filter: {
                            [name]: value
                        }
                    });
                } else {
                    setFormData({
                        ...formData,
                        filter: {
                            [name]: value
                        },
                        currentPage: 0
                    });
                }

            } catch (error) {

                let { message, errors, httpStatus } = extractError(error);
                if (httpStatus === 404) {
                    setFormData({
                        ...formData,
                        infoMessage: "No Records were found",
                        filter: {
                            [name]: value
                        }
                    });
                } else {
                    setFormData({
                        ...formData,
                        infoMessage: "",
                        filter: {
                            [name]: value
                        },
                        errors: {
                            message,
                            errors,
                            httpStatus
                        }
                    });
                }

            }
        } else {
            setFormData({
                ...formData,
                ...messageReset(),
                filter: {
                    [name]: value
                }
            });
        }
    }

    const handleDonationCauseChange = async (event) => {
        await handleChange(event);
    }
    const handleRecipientTypeChange = async (event) => {
        try {
            await handleChange(event)
        } catch (error) {
            let errors = extractError(error);
            setFormData({
                ...formData,
                errors: errors
            });
            return false;

        }
    }

    const importData = async (event) => {

        event.preventDefault();
        
        if (!formData.importFile) {
            let hasError = true;
            let error = "Select a file to upload"
            //alert(error);
            setFormValidityData({
                ...formValidityData,
                importFileError: hasError,
                importFileErrMsg: error
            })
            return false;
        }

        const fieldNames = ['donationCauseId', 'recipientType', 'maxAmount', 'sponsorshipType']
        for (let curFieldName of fieldNames) {
            //validate full form

            let field = document.getElementById(curFieldName);
            let fieldName = field.name;
            let fieldValue = field.value;
            let valResult = validateInput(fieldName, fieldValue);
            setFormValidityData({
                ...formValidityData,
                [fieldName + 'Error']: valResult.hasError,
                [fieldName + 'ErrMsg']: valResult.error
            })
            if (valResult.hasError) {
                alert("Form has validation error")
                return false;
            }

        }

        let sponsorshipMessage = {
            donationCauseId: formData.donationCauseId,
            sponsorshipType: formData.sponsorshipType,
            title: formData.title,
            maxAmount: parseFloat(formData.maxAmount) * 100,
            recipientType: formData.recipientType
        }
        const importData = new FormData();
        importData.append('file', formData.importFile);
        importData.append('sponsorship', JSON.stringify(sponsorshipMessage));

        try {
            // Send formData to your API endpoint using Axios
            await API.post(SPONSORSHIPS_URL + '/import', importData)
            let { sponsorships, totalPages } = await getSponsorships();
            setFormData({
                ...initialFormState,
                editMode: false,
                donationCauses: formData.donationCauses,
                donationCauseId: formData.donationCauseId,
                infoMessage: "The sponsorship data was uploaded successfully",
                sponsorships: sponsorships,
                totalPages: totalPages
            });
            if (importFileInput.current) {
                importFileInput.current.value = "";
                importFileInput.current.type = "file";
            }
            setFormValidityData(initialValidationState);

        } catch (error) {
            let errors = extractError(error);
            setFormData({
                ...formData,
                errors: errors
            });
        }
    };

    const handleSubmit = async (event) => {
        //prevent enter key
        if (event.key === 'Enter') {
            return false;
        }

        event.preventDefault();
        const tagNames = ['select', 'input', 'textarea'];
        for (let tagName of tagNames) {
            //validate full form
            for (let i = 0; i < event.target.getElementsByTagName(tagName).length; i++) {
                let fieldName = event.target.getElementsByTagName(tagName)[i].name;
                let fieldValue = event.target.getElementsByTagName(tagName)[i].value;
                let valResult = validateInput(fieldName, fieldValue);
                if (!valResult.hasError && recipientValidationFunction?.current) {
                    valResult = recipientValidationFunction.current(fieldName, fieldValue);
                }
                setFormValidityData({
                    ...formValidityData,
                    [fieldName + 'Error']: valResult.hasError,
                    [fieldName + 'ErrMsg']: valResult.error
                })
                if (valResult.hasError) {
                    alert("Form has validation error")
                    return false;
                }
            }
        }
        try {

            const postData = new FormData();
            let sponsorshipMessage = {
                donationCauseId: formData.donationCauseId,
                sponsorshipType: formData.sponsorshipType,
                sponsorshipKey: formData.sponsorshipKey,
                title: formData.title,
                sponsorshipAttributes: formData.sponsorshipAttributes,
                maxAmount: parseFloat(formData.maxAmount) * 100,
                recipientType: formData.recipientType
            }
            postData.append('image', formData.image);
            postData.append('sponsorship', JSON.stringify(sponsorshipMessage));

            if (formData.editMode) {
                await API.put(SPONSORSHIPS_URL + "/" + formData.id, postData);
            } else {
                await API.post(SPONSORSHIPS_URL, postData);
            }
            let { sponsorships, totalPages } = await getSponsorships();
            setFormData({
                ...initialFormState,
                editMode: false,
                donationCauses: formData.donationCauses,
                donationCauseId: formData.donationCauseId,
                sponsorships: sponsorships,
                totalPages: totalPages,
                infoMessage: "The sponsorship was saved successfully",
                randImageId: Date.now()
            });
            if (imageFileInput.current) {
                imageFileInput.current.value = "";
                imageFileInput.current.type = "file";
            }
            setFormValidityData(initialValidationState);

        } catch (error) {
            let errors = extractError(error);
            setFormData({
                ...formData,
                errors: errors
            });
            return false;
        }

    }

    const handleValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFormValidityData({
            ...formValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })
    }

    const handleImageFileChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const value = event.target.files[0];
        setFormData({
            ...formData,
            image: value,
            ...messageReset()
        })
    };

    const handleImageFileValidation = async (event) => {
        if (!formData.image) {
            return;
        }
        if (formData.image.size > 300 * 1024) {
            alert("Image file size cannot exceed 100kb");
            setFormValidityData({
                ...formValidityData,
                imageError: true,
                imageErrMsg: "Image file size cannot exceed 100kb"
            })
            return false;
        }

        setFormValidityData({
            ...formValidityData,
            imageError: false,
            imageErrMsg: ""
        })
    }


    const validateInput = (name, value) => {
        let hasError = false,
            error = ""
        switch (name) {
            case "donationCauseId":
                if (value.trim() === "") {
                    hasError = true
                    error = "Donation Cause name cannot be empty"
                } else if (!isValidUUID(value)) {
                    hasError = true
                    error = "Invalid Donation Cause";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "recipientType":
                if (value.trim() === "") {
                    hasError = true
                    error = "Recipient Type name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid Recipient Type. Only alphbets are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "title":
                if (value.trim() === "") {
                    hasError = true
                    error = "Title cannot be empty for manual data-entry"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid Title. Only alphbets are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "sponsorshipType":
                if (value.trim() === "") {
                    hasError = true
                    error = "Sponsorship Type name cannot be empty"
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Invalid Sponsorship Type. Only alphbets are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "maxAmount":
                if (value.trim() === "") {
                    hasError = true
                    error = "Max Amount cannot be empty"
                } else if (!isNumeric(value)) {
                    hasError = true
                    error = "Invalid Max Amount. Only numeric are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            default:
                break;
        }
        return { hasError, error };
    }

    const editSponsorship = async (event, sponsorship) => {
        setFormData({
            ...formData,
            ...messageReset(),
            ...sponsorship,
            editMode: true
        })
    }

    const deleteSponsorship = async (event, sponsorship) => {
        try {
            await API.delete(SPONSORSHIPS_URL + "/" + sponsorship.id);
            let { sponsorships, totalPages } = await getSponsorships();
            setFormData({
                ...initialFormState,
                editMode: false,
                donationCauses: formData.donationCauses,
                donationCauseId: formData.donationCauseId,
                sponsorships: sponsorships,
                totalPages: totalPages,
                infoMessage: "The sponsorship was deleted successfully"
            });

            setFormValidityData(initialValidationState);

        } catch (error) {
            let errors = extractError(error);
            if (errors.httpStatus === 404) {
                setFormData({
                    ...formData,
                    infoMessage: "",
                    sponsorships: [],
                    totalPages: 0,
                    errors: {}
                });
                return;
            }
            setFormData({
                ...formData,
                errors: errors
            });
            return false;
        }

    }

    const handlePaging = async (page) => {
        if (page < 0) {
            alert("Reached the beginning of data set")
            page = 0;
        }
        if (page > formData.totalPages - 1) {
            alert("Reached the end of data set")
            page = formData.totalPages - 1;
        }
        setFormData({
            ...formData,
            ...messageReset(),
            currentPage: page
        })
    }

    const handleImportFileChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const value = event.target.files[0];
        setFormData({
            ...formData,
            importFile: value,
            ...messageReset()
        })
    };

    const handleImportFileValidation = async (event) => {
        if (!formData.importFile) {
            return;
        }
        if (formData.importFile.size > 50 * 1024 * 1024) {
            alert("Import file size cannot exceed 50 MB");
            setFormValidityData({
                ...formValidityData,
                importFileError: true,
                importFileErrMsg: "Import file size cannot exceed 200MB"
            })
            return false;
        }

        setFormValidityData({
            ...formValidityData,
            importFileError: false,
            importFileErrMsg: ""
        })
    }

    const deleteSponsorshipImage = async (event, sponsorshipId) => {
        try {
            await API.delete(SPONSORSHIPS_URL + "/" + sponsorshipId + "/img");
            let { sponsorships, totalPages } = await getSponsorships();
            setFormData({
                ...initialFormState,
                editMode: false,
                donationCauses: formData.donationCauses,
                donationCauseId: formData.donationCauseId,
                sponsorships: sponsorships,
                totalPages: totalPages,
                infoMessage: "The sponsorship image was deleted successfully"
            });

            setFormValidityData(initialValidationState);

        } catch (error) {
            let errors = extractError(error);
            if (errors.httpStatus === 404) {
                setFormData({
                    ...formData,
                    sponsorships: [],
                    totalPages: 0,
                    errors: {}
                });
                return;
            }
            setFormData({
                ...formData,
                errors: errors
            });
            return false;
        }
    }

    return (
        <React.Fragment>
            <FormContainer title="Donation Cause Sponsorship" submitButtonFunction={handleSubmit}
                errors={formData.errors} infoMessage={formData.infoMessage} successMessage={formData.infoMessage}
                toastCloseFunc={onToastClose}>

                <Row>
                    <Col mb={4} lg={4} sm={6}>
                        <FormField name="donationCauseId" label="Donation Cause" type="select" readOnly={formData.readOnly}
                            onChange={handleDonationCauseChange} onBlur={handleValidation} value={formData.donationCauseId}
                            valstate={!formValidityData.donationCauseIdError} valMsg={formValidityData.donationCauseIdErrMsg}>
                            <option value="" key="zeroval"></option>
                            {formData.donationCauses.map((donationCause, idx) => (
                                <option value={donationCause.id} key={idx}>{donationCause.donationCauseDescription}</option>
                            ))}
                        </FormField>
                    </Col>
                </Row>

                <Row>
                    <Col mb={4} lg={4} sm={6}>
                        <FormField name="recipientType" label="Recipient Type" type="select" readOnly={formData.readOnly}
                            onChange={handleRecipientTypeChange} onBlur={handleValidation} value={formData.recipientType}
                            valstate={!formValidityData.recipientTypeError} valMsg={formValidityData.recipientTypeErrMsg}>
                            <option value="" key="zeroval">Select Recipient Type</option>
                            {businessConfig.recipientTypes.map((rcpntType, idx) => (
                                <option value={rcpntType} key={idx}>{getRecipientTypes(rcpntType)}</option>
                            ))}


                        </FormField>

                    </Col>

                </Row>
                <Row>
                    <Col>
                        <FormField name="title" label="Title" type="text" value={formData.title}
                            placeholder="Title" onChange={handleChange} onBlur={handleValidation}
                            valstate={!formValidityData.titleError} valMsg={formValidityData.titleErrMsg}
                            readOnly={formData.readOnly} />
                    </Col>
                    <Col>

                        <FormField name="sponsorshipType" label="Sponsorship Type" type="select" readOnly={formData.readOnly}
                            onChange={handleChange} onBlur={handleValidation} value={formData.sponsorshipType}
                            valstate={!formValidityData.sponsorshipTypeError} valMsg={formValidityData.sponsorshipTypeErrMsg}>
                            <option value="" key="zeroval"></option>
                            <option value="RECURRING" key="RECURRING">Recurring</option>
                            <option value="ONE_TIME" key="ONE_TIME">One Time</option>
                        </FormField>

                    </Col>
                    <Col>
                        <FormField name="maxAmount" label="Amount" type="text" value={formData.maxAmount}
                            placeholder="Amount" onChange={handleChange} onBlur={handleValidation}
                            valstate={!formValidityData.maxAmountError} valMsg={formValidityData.maxAmountErrMsg}
                            readOnly={formData.readOnly} />
                    </Col>
                </Row>

                <Row>
                    <Col><hr />
                        You can either upload a csv or xls file using the import option or you can add individual records using the fields in next section.
                        <br />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FilesField name="importFile" placeholder="Import sponsorship Data" accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleImportFileChange} valstate={!formValidityData.importFileError} valMsg={formValidityData.importFileErrMsg}
                            rref={importFileInput} onBlur={handleImportFileValidation} />
                    </Col>
                    <Col>
                        <br />
                        <Button variant="primary" onClick={importData} className={'btn-fill'}>Import</Button>
                    </Col>
                </Row>
                <Row>
                    <Col><hr /></Col>
                </Row>

                {formData.recipientType &&
                    <React.Fragment>
                        <Row>
                            <Col>

                                <FilesField name="image" label="Image" placeholder="Image" accept="image/*" onChange={handleImageFileChange}
                                    rref={imageFileInput} onBlur={handleImageFileValidation}
                                    valstate={!formValidityData.imageError} valMsg={formValidityData.imageErrMsg} />
                            </Col>

                        </Row>
                        <RecipientForm domain={businessConfig.subdomain} recipientType={formData.recipientType} formData={formData} setFormData={setFormData}
                            formValidityData={formValidityData} setFormValidityData={setFormValidityData} recipientValidationFunction={recipientValidationFunction} />
                    </React.Fragment>}
                <Row>
                    <Col>
                        <SubmitButton label={(formData.editMode ? "Save" : "Add") + " Sponsorship Recipient"} disabled={formData.readOnly} className="mt-4" />
                        &nbsp;<CancelButton label="Cancel" disabled={formData.readOnly} className="mt-4" onClick={(event) => {
                            setFormData({
                                ...initialFormState,
                                donationCauses: formData.donationCauses
                            })
                        }} />

                    </Col>
                </Row>


                <Card style={{ flexGrow: 1 }}>
                    <Card.Body>
                        <Row>
                            <Col mb={4} lg={4} sm={6}><Form.Control name="titleFilter" label="Keyword Filter" placeholder="Keyword Filter" type="text" onChange={handleFilter}
                                onBlur={handleFilter} onKeyDown={handleFilterKeyDown} value={formData.filter.titleFilter}
                            />
                            </Col>
                            {!_.isEmpty(formData.sponsorships) &&
                                <Col className='d-flex justify-content-end'>
                                    <Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(formData.currentPage - 1) }}>
                                        <h6><FontAwesomeIcon icon={faArrowLeft} />Previous Page</h6>
                                    </Button>
                                    &nbsp;<span className="border-0 text-dark btn"><h6>{formData.currentPage + 1} of {formData.totalPages}</h6></span>
                                    &nbsp;<Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(formData.currentPage + 1) }}>
                                        <h6>Next Page<FontAwesomeIcon icon={faArrowRight} /></h6>
                                    </Button>
                                </Col>}
                        </Row>
                        <Row>
                            {formData.sponsorships && !_.isEmpty(formData.sponsorships) && formData.sponsorships.map((sponsorship, idx) => {
                                let rowClassName = ''
                                if (sponsorship.status === 'SPONSORED') {
                                    rowClassName = 'text-warning';
                                } else if (sponsorship.status === 'FULFILLED') {
                                    rowClassName = 'text-success';
                                }

                                let firstKey = '';
                                let firstValue = '';
                                if (!_.isEmpty(sponsorship.sponsorshipAttributes)) {
                                    const keys = Object.keys(sponsorship.sponsorshipAttributes);
                                    if (keys.length > 0) {
                                        firstKey = keys[0];
                                        firstValue = sponsorship.sponsorshipAttributes[firstKey];
                                    }
                                }
                                return (
                                    <Col key={idx} xs={12} sm={6} md={3} lg={2}>
                                        <Card>
                                            {sponsorship.mediaType && <Card.Img src={process.env.REACT_APP_BASE_API_URL + SPONSORSHIPS_URL + '/' + sponsorship.id + '/img?i=' + formData.randImageId}
                                                style={{ height: '250px', maxHeight: '250px', objectFit: 'cover' }} />}
                                            {!sponsorship.mediaType && <Card.Img src="/ui/images/no-image.svg"
                                                style={{ height: '250px', maxHeight: '250px', objectFit: 'cover' }} />}
                                            <Card.Body>
                                                <Card.Title style={{ fontWeight: "bold" }}>{sponsorship.title}</Card.Title>
                                                <Card.Text>
                                                    <small>
                                                        <Row><Col>
                                                            {firstKey && <Button variant="link" className="border-0 text-dark m-0 p-0" onClick={event => handleSponsorshipDetailShow(sponsorship)}>
                                                                {splitCamelCase(firstKey)}: {firstValue}
                                                            </Button>}
                                                        </Col></Row>
                                                        <Row>
                                                            <Col>
                                                                {getSponsorshipTypes(sponsorship.sponsorshipType)} {getRecipientTypes(sponsorship.recipientType)} Sponsorship at {formatCurrency(sponsorship.maxAmount, businessConfig.locale, businessConfig.currency)} {sponsorship.sponsorshipType === 'RECURRING' && <span>Monthly</span>}
                                                            </Col>
                                                        </Row>
                                                        <Row className={rowClassName}>
                                                            <Col>Status:
                                                                {getSponsorshipStatus(sponsorship.status)}
                                                                {(sponsorship.status === 'SPONSORED' || sponsorship.status === 'FULFILLED') && <> by {sponsorship.sponsorEmailAddress} on {formatDate(sponsorship.sponsorshipDate)}</>}
                                                            </Col>
                                                        </Row>
                                                    </small>
                                                </Card.Text>
                                            </Card.Body>
                                            <Card.Footer>
                                                <Row>
                                                    <Col>
                                                        <Button variant="dark" className="border-0 text-dark" onClick={evt => editSponsorship(evt, sponsorship)}
                                                            disabled={sponsorship.status === 'FULFILLED'}>
                                                            <FontAwesomeIcon icon={faEdit} title="Edit Sponsorship" />
                                                        </Button>
                                                        <Button variant="dark" className="border-0 text-dark" onClick={evt => deleteSponsorship(evt, sponsorship)}
                                                            disabled={sponsorship.status !== 'NOT_SPONSORED'}>
                                                            <FontAwesomeIcon icon={faTrash} title="Delete Sponsorship" />
                                                        </Button>
                                                        {sponsorship.mediaType && <Button variant="dark" className="border-0 text-dark" onClick={evt => deleteSponsorshipImage(evt, sponsorship.id)}
                                                            disabled={!sponsorship.active}>
                                                            <span className="fa-stack">
                                                                <FontAwesomeIcon icon={faImage} className='fa-solid fa-stack-1x' title="delete image" />
                                                                <FontAwesomeIcon icon={faBan} className='fa-solid fa-stack-2x' style={{ color: "Tomato" }} title="delete image" />
                                                            </span>
                                                        </Button>}
                                                    </Col>
                                                </Row>

                                            </Card.Footer>
                                        </Card>
                                    </Col>
                                )
                            })}
                        </Row>
                    </Card.Body>
                </Card>
                <Modal show={showSponsorshipImage.show} onHide={handleSponsorshipImageClose}>
                    <Modal.Header closeButton />
                    <Modal.Body>
                        {showSponsorshipImage.sponsorship.mediaType &&
                            <img src={process.env.REACT_APP_BASE_API_URL + SPONSORSHIPS_URL + '/' + showSponsorshipImage.sponsorship.id + '/img'} />}
                        {(!showSponsorshipImage.sponsorship.mediaType) &&
                            <>No Image is assinged to this donation cause</>
                        }
                    </Modal.Body>
                </Modal>
                <Modal show={showSponsorshipDetail.show} onHide={handleSponsorshipDetailClose}>
                    <Modal.Header closeButton>{getRecipientTypes(showSponsorshipDetail.sponsorship.recipientType)} Details</Modal.Header>
                    <Modal.Body>
                        {showSponsorshipDetail.sponsorship.sponsorshipAttributes && Object.entries(showSponsorshipDetail.sponsorship.sponsorshipAttributes).map(([key, value]) => (
                            <React.Fragment key={key}>
                                <strong key={key}>{splitCamelCase(key)}:</strong> {value} <br />
                            </React.Fragment>
                        ))}
                        {(_.isEmpty(showSponsorshipDetail.sponsorship.sponsorshipAttributes)) &&
                            <>No Details availaible for this sponsorship</>
                        }
                    </Modal.Body>
                </Modal>
            </FormContainer>
        </React.Fragment>
    )
}

export default BusinessSponsorship